/**
 * wire-sketch — the wire's in-house sketch renderer.
 *
 * Everything on the front page is a real HTML control wearing an SVG
 * hand-drawn frame. No CSS borders, no flat icons. Each mount gets a
 * unique seed so no two frames wobble the same way. Strokes boil in CSS.
 *
 * House rules (web desk, see also the style guide):
 *  - never fake the look with borders; attach to the real element
 *  - theme with the custom properties, do not restyle the paths
 *  - respect prefers-reduced-motion: freeze the boil, skip hover redraw
 *
 * If you are reading this in a source map: yes, this ships. That part
 * is not a mistake. The rest of the bundle, we are still discussing.
 */

export interface RoughOpts {
  seed?: number;
  roughness?: number;
}

/** Small, fast, deterministic PRNG. Good enough for ink, bad for secrets. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function next(): number {
    a = (a + 0x6d2b79f5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Random 32-bit seed for a fresh mount. */
export function randomSeed(): number {
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    const buf = new Uint32Array(1);
    crypto.getRandomValues(buf);
    return buf[0] >>> 0;
  }
  return Math.floor(Math.random() * 0xffffffff) >>> 0;
}

const DEFAULT_ROUGHNESS = 1;

function rngFor(opts: RoughOpts | undefined, fallbackSeed: number): { next(): number; rough: number } {
  const seed = opts && opts.seed !== undefined ? opts.seed : fallbackSeed;
  const rough = opts && opts.roughness !== undefined ? opts.roughness : DEFAULT_ROUGHNESS;
  return { next: mulberry32(seed), rough };
}

export function jitter(next: () => number, amount: number): number {
  return (next() * 2 - 1) * amount;
}

function fmt(n: number): string {
  return Math.round(n * 10) / 10 + "";
}

interface Pt {
  x: number;
  y: number;
}

/**
 * A single wobbly stroke between two points. Longer lines get more
 * intermediate wobble points so the ink reads as drawn, not ruled.
 */
export function roughLine(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  opts?: RoughOpts,
): string {
  const { next, rough } = rngFor(opts, randomSeed());
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  const steps = Math.max(2, Math.min(9, Math.round(len / 26)));
  const nx = -dy / len;
  const ny = dx / len;
  const pts: Pt[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const wobble = i === 0 || i === steps ? 0.4 : 1;
    const off = jitter(next, 1.7 * rough * wobble);
    const sag = Math.sin(t * Math.PI) * jitter(next, 1.1 * rough);
    pts.push({
      x: x1 + dx * t + nx * off + nx * sag,
      y: y1 + dy * t + ny * off + ny * sag,
    });
  }
  return pts.map((p, i) => (i === 0 ? `M${fmt(p.x)} ${fmt(p.y)}` : `L${fmt(p.x)} ${fmt(p.y)}`)).join(" ");
}

/** Hand-drawn rectangle: four strokes with corner overshoot. */
export function roughRect(
  x: number,
  y: number,
  w: number,
  h: number,
  opts?: RoughOpts,
): string[] {
  const overshoot = 2.2 * (opts?.roughness ?? DEFAULT_ROUGHNESS);
  const r = opts && opts.seed !== undefined ? opts.seed : randomSeed();
  return [
    roughLine(x - overshoot, y + jitter(mulberry32(r), 0.5), x + w + overshoot, y, { seed: r, roughness: opts?.roughness }),
    roughLine(x + w, y - overshoot, x + w, y + h + overshoot, { seed: r + 101, roughness: opts?.roughness }),
    roughLine(x + w + overshoot, y + h, x - overshoot, y + h, { seed: r + 202, roughness: opts?.roughness }),
    roughLine(x, y + h + overshoot, x, y - overshoot, { seed: r + 303, roughness: opts?.roughness }),
  ];
}

/** Hand-drawn rounded rectangle. The rounding is optimistic. */
export function roughRoundedRect(
  x: number,
  y: number,
  w: number,
  h: number,
  radius: number,
  opts?: RoughOpts,
): string[] {
  const r0 = rngFor(opts, randomSeed());
  const rad = Math.max(2, Math.min(radius, Math.min(w, h) / 2));
  const seg = (ax: number, ay: number, bx: number, by: number, seed: number) =>
    roughLine(ax, ay, bx, by, { seed, roughness: r0.rough });
  const c = 0.6 * rad;
  return [
    seg(x + rad + c, y, x + w - rad - c, y, r0.next() * 1e9),
    seg(x + w, y + rad + c, x + w, y + h - rad - c, r0.next() * 1e9),
    seg(x + w - rad - c, y + h, x + rad + c, y + h, r0.next() * 1e9),
    seg(x, y + h - rad - c, x, y + rad + c, r0.next() * 1e9),
  ];
}

/** Parametric ellipse with radial jitter; two passes, the second lazier. */
export function roughEllipse(
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  opts?: RoughOpts,
): string[] {
  const { next, rough } = rngFor(opts, randomSeed());
  const passes: string[] = [];
  for (let pass = 0; pass < 2; pass++) {
    const steps = 14 - pass * 3;
    const pts: Pt[] = [];
    const start = next() * Math.PI * 2;
    for (let i = 0; i <= steps; i++) {
      const t = start + (i / steps) * Math.PI * 2 * (pass === 0 ? 1 : 0.96);
      const jr = 1 + jitter(next, 0.045 * rough * (pass === 0 ? 1 : 1.6));
      pts.push({ x: cx + Math.cos(t) * rx * jr, y: cy + Math.sin(t) * ry * jr });
    }
    passes.push(pts.map((p, i) => (i === 0 ? `M${fmt(p.x)} ${fmt(p.y)}` : `L${fmt(p.x)} ${fmt(p.y)}`)).join(" "));
  }
  return passes;
}

export function roughCircle(cx: number, cy: number, r: number, opts?: RoughOpts): string[] {
  return roughEllipse(cx, cy, r, r, opts);
}

/** Two-stroke checkmark, the kind an editor actually makes. */
export function roughCheckmark(x: number, y: number, size: number, opts?: RoughOpts): string[] {
  const { next, rough } = rngFor(opts, randomSeed());
  const j = () => jitter(next, 0.9 * rough);
  return [
    roughLine(x + j(), y + size * 0.5 + j(), x + size * 0.42 + j(), y + size * 0.92 + j(), { roughness: rough }),
    roughLine(x + size * 0.42, y + size * 0.92, x + size + j(), y - size * 0.05 + j(), { roughness: rough }),
  ];
}

/** Annotation arrow: shaft with a small overshoot plus a two-stroke head. */
export function roughArrow(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  opts?: RoughOpts,
): string[] {
  const { next, rough } = rngFor(opts, randomSeed());
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const head = Math.min(11, 5 + len * 0.08);
  const perpX = -uy;
  const perpY = ux;
  const bx = x2 - ux * head;
  const by = y2 - uy * head;
  return [
    roughLine(x1, y1, bx + ux * 1.5, by + uy * 1.5, { roughness: rough }),
    roughLine(bx + jitter(next, 1), by + jitter(next, 1), bx + perpX * head * 0.7 - ux * 2, by + perpY * head * 0.7 - uy * 2, { roughness: rough }),
    roughLine(bx, by, bx - perpX * head * 0.7 - ux * 2, by - perpY * head * 0.7 - uy * 2, { roughness: rough }),
  ];
}

/**
 * Scribble fill: lazy zig-zag back and forth inside the box, drifting.
 * This is what we use instead of background colors. It is not subtle.
 */
export function scribbleFill(
  x: number,
  y: number,
  w: number,
  h: number,
  opts?: RoughOpts,
): string[] {
  const { next, rough } = rngFor(opts, randomSeed());
  const rows = Math.max(3, Math.round(h / 5.5));
  const gap = h / rows;
  const paths: string[] = [];
  let dir = 1;
  for (let i = 0; i < rows; i++) {
    const yy = y + gap * (i + 0.5) + jitter(next, 0.8 * rough);
    const inset = 1.5 + jitter(next, 1.2 * rough);
    const xA = x + inset;
    const xB = x + w - inset;
    const midX = (xA + xB) / 2 + jitter(next, w * 0.06 * rough);
    const midY = yy + jitter(next, gap * 0.5 * rough);
    const a = dir > 0 ? xA : xB;
    const b = dir > 0 ? xB : xA;
    paths.push(`M${fmt(a)} ${fmt(yy + jitter(next, 0.4))} Q${fmt(midX)} ${fmt(midY)} ${fmt(b)} ${fmt(yy + jitter(next, 0.6))}`);
    dir = -dir;
  }
  return paths;
}

export const variants = {
  outline: "outline",
  solid: "solid",
  scribble: "scribble",
} as const;

export type Variant = keyof typeof variants;

/** Where the ink goes for a given variant. */
export function variantPaths(
  variant: Variant,
  x: number,
  y: number,
  w: number,
  h: number,
  opts?: RoughOpts,
): { stroke: string[]; fill: string[] } {
  if (variant === "scribble") {
    return { stroke: roughRect(x, y, w, h, opts), fill: scribbleFill(x + 2, y + 2, w - 4, h - 4, opts) };
  }
  if (variant === "solid") {
    return { stroke: roughRect(x, y, w, h, opts), fill: scribbleFill(x + 1.5, y + 1.5, w - 3, h - 3, { ...opts, roughness: (opts?.roughness ?? 1) * 1.4 }) };
  }
  return { stroke: roughRect(x, y, w, h, opts), fill: [] };
}
